import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { EyeIcon } from "lucide-react";

import type { ChallengeYourIdea } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { belowComputer, computerUp, useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { ButtonLink } from "simplydo/react/ui/button";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import IdeasImage from "src/images/ideas.png";
import { useCurrentUser } from "utils/currentUser";

import NewChallengeModal from "components/challenges/NewChallengeModal";
import YourIdeasFilters from "components/lib/Filters/YourIdeasFilters";
import IdeaCardView from "components/lib/Ideas/IdeaView/IdeaCardView";
import type { LibIdeaViewIdea } from "components/lib/Ideas/types";
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray";
import { SideNav } from "components/lib/SideNav";
import { EmptyBox } from "components/lib/UI";

export const SideNavContainer = styled.div`
  ${belowComputer} {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    width: 100%;
    margin: 12.5px 0 2.5px 0;
  }

  ${computerUp} {
    margin-top: 10px;
  }
`;

export type IYourIdeaFilters = {
  sort: string;
  showDraft: boolean;
  showSubmitted: boolean;
  filter: string;
};

const YourIdeas = () => {
  const [ideas, setIdeas] = useState<ChallengeYourIdea[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [newChallengeOpen, setNewChallengeOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<IYourIdeaFilters>({
    sort: "newest",
    showDraft: true,
    showSubmitted: true,
    filter: "",
  });
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { sort, showDraft, showSubmitted, filter } = filters;

  useEffect(() => {
    setIdeas([]);
    if (user?._id) {
      setLoading(true);
      api.users.ideas(
        user._id,
        null,
        (data) => {
          setLoading(false);
          setIdeas(data.ideas);
        },
        () => {},
      );
    }
    return () => {
      api.journey.record("ideas", null, "viewIdeasPage");
    };
  }, [user?._id, user?.ownerOrganisation?._id]);

  const filteredIdeas = ideas.filter((idea) => {
    if (!showDraft && !idea.isSubmitted) return false;
    if (!showSubmitted && idea.isSubmitted) return false;
    if (idea.ownerTags?.find((tag) => tag.value.match(RegExp(filter, "i"))))
      return true;
    if (idea.name?.match(RegExp(filter, "i"))) return true;
    if (idea.ownerChallenge?.name.includes(filter)) return true;
    return false;
  });
  if (sort === "oldest") filteredIdeas.reverse();
  return (
    <div>
      <NewChallengeModal
        newChallengeOpen={newChallengeOpen}
        onCloseModal={() => setNewChallengeOpen(false)}
      />
      <Helmet title={`Your ${t("generic.ideas")}`} />

      <SideNav
        navItems={[
          theme.sizes.isComputer
            ? {
                key: "filters",
                type: "custom" as const,
                content: (
                  <YourIdeasFilters
                    setFilters={setFilters}
                    showDraft={showDraft}
                    showSubmitted={showSubmitted}
                    sort={sort}
                    filter={filter}
                  />
                ),
              }
            : null,
        ].filter((item) => Boolean(item))}
        main={
          <>
            <div style={{ marginBottom: "1rem" }}>
              <H1 style={{ color: "#3b3b3b", margin: "0px" }}>
                {t("challenges.yours.ideas.title")}
              </H1>
              {(user?.ownerOrganisations?.length ?? 0) > 1 ? (
                <div
                  style={{
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "0.25em",
                  }}
                >
                  <span>
                    in{" "}
                    <span style={{ fontWeight: 600 }}>
                      {user?.ownerOrganisation?.name}
                    </span>
                    {" -"}
                  </span>
                  <OrgSwitcherDropdown
                    basic
                    trigger={
                      <a
                        tabIndex={0}
                        role="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        Change organisation
                      </a>
                    }
                  />
                </div>
              ) : null}
            </div>
            {!theme.sizes.isComputer && (
              <Accordion style={{ marginBottom: 15 }}>
                <AccordionTitle>
                  {t("common:capitalise", { key: "generic.idea" })} Filters
                </AccordionTitle>
                <AccordionContent>
                  <YourIdeasFilters
                    setFilters={setFilters}
                    showDraft={showDraft}
                    showSubmitted={showSubmitted}
                    sort={sort}
                    filter={filter}
                  />
                </AccordionContent>
              </Accordion>
            )}
            {loading && !filteredIdeas?.length && <Loader />}
            {!loading && !filteredIdeas.length && !filteredIdeas.length && (
              <EmptyBox>
                <img
                  src={IdeasImage}
                  style={{
                    maxHeight: 150,
                    maxWidth: "70%",
                    display: "block",
                    margin: "10px auto",
                  }}
                  alt="Discover"
                />
                <H2>
                  {t("ideas.yours.sidebar.noneCreated")}
                  <Subheader>{t("ideas.yours.sidebar.start")}</Subheader>
                </H2>
                <ButtonLink color="primary" to="/challenges/discover">
                  <Icon icon={EyeIcon} />
                  {t("challenges.discover.title")}
                </ButtonLink>
              </EmptyBox>
            )}
            {!loading && !filteredIdeas.length && filteredIdeas.length > 0 ? (
              <EmptyBox>
                <img
                  src={IdeasImage}
                  style={{
                    maxHeight: 150,
                    maxWidth: "70%",
                    display: "block",
                    margin: "10px auto",
                  }}
                  alt="Discover"
                />
                <H2>{t("ideas.yours.sidebar.noResults")}</H2>
              </EmptyBox>
            ) : null}
            <Grid columns={4} mobile={1}>
              {filteredIdeas.map((idea) =>
                idea.ownerChallenge ? (
                  <GridColumn key={idea._id}>
                    <IdeaCardView
                      idea={idea as LibIdeaViewIdea}
                      challenge={idea.ownerChallenge}
                      ideasVotedFor={0}
                    />
                  </GridColumn>
                ) : null,
              )}
            </Grid>
            <div style={{ marginTop: 30, textAlign: "center" }}>
              <Link
                to="/challenges/discover"
                style={{
                  color: theme.primaryColour,
                  textDecorationLine: "underline",
                  bottom: 0,
                }}
              >
                {t("challenges.discover.title")}
              </Link>
            </div>
          </>
        }
      />
    </div>
  );
};

export default YourIdeas;
